import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Tab,
  Tabs,
  Typography,
  Paper,
  Grid,
  CircularProgress,
  Alert,
  Button,
  ButtonGroup,
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { format, startOfDay, endOfDay, startOfWeek, endOfWeek, startOfMonth, endOfMonth } from 'date-fns';
import { rideApi, RideFilters } from '../../apis/rideApi';
import { Ride, RideType, RideStatus } from '../../types/ride';
import RideList from './components/RideList';
import StatusFilter from './components/StatusFilter';
import SidePanel from '../../components/SidePanel';
import TopBar from '../../components/TopBar';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`ride-tabpanel-${index}`}
      aria-labelledby={`ride-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ py: 3 }}>{children}</Box>}
    </div>
  );
}

const rideTypes = [
  { id: RideType.NORMAL, label: 'Normal Rides' },
  { id: RideType.SHARED, label: 'Shared Rides' },
  { id: RideType.DELIVERY, label: 'Delivery Rides' },
];

const filterTypes = [
  { value: 'daily', label: 'Daily' },
  { value: 'weekly', label: 'Weekly' },
  { value: 'monthly', label: 'Monthly' },
  { value: 'custom', label: 'Custom Range' },
];

export default function RidesPage() {
  const navigate = useNavigate();
  const [selectedTab, setSelectedTab] = useState(0);
  const [filters, setFilters] = useState<RideFilters>({
    page: 1,
    limit: 10,
    filter_type: 'daily',
  });
  const [rides, setRides] = useState<Ride[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [pagination, setPagination] = useState({
    total: 0,
    page: 1,
    limit: 10,
    total_pages: 0,
  });

  const fetchRides = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await rideApi.getRides({
        ...filters,
        ride_type: rideTypes[selectedTab].id,
      });
      setRides(response.rides);
      setPagination(response.pagination);
    } catch (err) {
      setError('Failed to load rides. Please try again later.');
      console.error('Error fetching rides:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchRides();
  }, [filters, selectedTab]);

  const handleFilterChange = (newFilters: Partial<RideFilters>) => {
    setFilters(prev => ({ ...prev, ...newFilters, page: 1 }));
  };

  const handlePageChange = (page: number) => {
    setFilters(prev => ({ ...prev, page }));
  };

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setSelectedTab(newValue);
    setFilters(prev => ({ ...prev, page: 1 }));
  };

  const handleFilterTypeChange = (filterType: string) => {
    const today = new Date();
    let startDate: Date | undefined;
    let endDate: Date | undefined;

    switch (filterType) {
      case 'daily':
        startDate = startOfDay(today);
        endDate = endOfDay(today);
        break;
      case 'weekly':
        startDate = startOfWeek(today);
        endDate = endOfWeek(today);
        break;
      case 'monthly':
        startDate = startOfMonth(today);
        endDate = endOfMonth(today);
        break;
      case 'custom':
        // For custom range, we'll let the user select dates
        startDate = undefined;
        endDate = undefined;
        break;
    }

    handleFilterChange({
      filter_type: filterType as RideFilters['filter_type'],
      start_date: startDate ? format(startDate, 'yyyy-MM-dd') : undefined,
      end_date: endDate ? format(endDate, 'yyyy-MM-dd') : undefined,
    });
  };

  const handleDateRangeChange = (type: 'start' | 'end', date: Date | null) => {
    if (date) {
      handleFilterChange({
        [`${type}_date`]: format(date, 'yyyy-MM-dd'),
        filter_type: 'custom',
      });
    }
  };

  return (
    <Box className="mainContainer">
      <Box className="sidePanelContainer">
        <SidePanel isOpen={true} onClose={() => {}} />
      </Box>
      <Box className="dashboardContainer">
        <TopBar />
        <Box sx={{ width: "100%", padding: "20px", marginTop: "5%" }}>
          <Paper elevation={3} sx={{ padding: "20px" }}>
            <Typography variant="h4" component="h1" gutterBottom>
              Rides Management
            </Typography>
            <Typography variant="body1" color="text.secondary" paragraph>
              View and manage all rides across different categories
            </Typography>

            <Tabs
              value={selectedTab}
              onChange={handleTabChange}
              variant="fullWidth"
              sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}
            >
              {rideTypes.map((type) => (
                <Tab key={type.id} label={type.label} />
              ))}
            </Tabs>

            <Box sx={{ mb: 3 }}>
              <Grid container spacing={3} alignItems="center">
                <Grid item xs={12}>
                  <ButtonGroup variant="outlined" size="small">
                    {filterTypes.map((type) => (
                      <Button
                        key={type.value}
                        onClick={() => handleFilterTypeChange(type.value)}
                        variant={filters.filter_type === type.value ? 'contained' : 'outlined'}
                      >
                        {type.label}
                      </Button>
                    ))}
                  </ButtonGroup>
                </Grid>

                {filters.filter_type === 'custom' && (
                  <>
                    <Grid item xs={12} md={6}>
                      <LocalizationProvider dateAdapter={AdapterDateFns}>
                        <DatePicker
                          label="Start Date"
                          value={filters.start_date ? new Date(filters.start_date) : null}
                          onChange={(date) => handleDateRangeChange('start', date)}
                          maxDate={filters.end_date ? new Date(filters.end_date) : undefined}
                          slotProps={{
                            textField: {
                              fullWidth: true,
                              size: 'small',
                            },
                          }}
                        />
                      </LocalizationProvider>
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <LocalizationProvider dateAdapter={AdapterDateFns}>
                        <DatePicker
                          label="End Date"
                          value={filters.end_date ? new Date(filters.end_date) : null}
                          onChange={(date) => handleDateRangeChange('end', date)}
                          minDate={filters.start_date ? new Date(filters.start_date) : undefined}
                          slotProps={{
                            textField: {
                              fullWidth: true,
                              size: 'small',
                            },
                          }}
                        />
                      </LocalizationProvider>
                    </Grid>
                  </>
                )}

                <Grid item xs={12}>
                  <StatusFilter
                    status={filters.ride_status}
                    onStatusChange={(status) => handleFilterChange({ ride_status: status })}
                  />
                </Grid>
              </Grid>
            </Box>

            {error && (
              <Alert severity="error" sx={{ mb: 3 }}>
                {error}
              </Alert>
            )}

            {loading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
                <CircularProgress />
              </Box>
            ) : (
              rideTypes.map((type, index) => (
                <TabPanel key={type.id} value={selectedTab} index={index}>
                  <RideList
                    rides={rides}
                    pagination={pagination}
                    onPageChange={handlePageChange}
                    onRideClick={(rideId) => navigate(`/rides/${rideId}`)}
                  />
                </TabPanel>
              ))
            )}
          </Paper>
        </Box>
      </Box>
    </Box>
  );
} 